import { useAuthStore } from '@/app/auth/stores/auth.store';
import { router } from '@/app/router/router';
import { useEffect, useRef, useState, type PropsWithChildren } from 'react';

/**
 * Boots FCM after login. Mounted at root in AppProviders.tsx so every
 * authenticated route can receive push notifications. No-op on web.
 */
export const PushProvider = ({ children }: PropsWithChildren) => {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated());
  const [token, setToken] = useState<string | null>(null);
  const cleanupRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    let cancelled = false;
    const cap = globalThis as typeof globalThis & {
      Capacitor?: { isNativePlatform?: () => boolean };
    };
    const isNative = cap.Capacitor?.isNativePlatform?.() === true;
    if (!isNative) {
      return;
    }

    import('./push').then(({ initPush, registerDeviceToken }) => {
      if (cancelled) {
        return;
      }
      void initPush({
        onToken: (value) => {
          setToken(value);
          void registerDeviceToken(value);
          console.info('[push] FCM device token:', value);
        },
        onMessage: ({ route }) => {
          if (route) {
            router.navigate(route);
          }
        },
      }).then(({ dispose }) => {
        if (cancelled) {
          dispose();
        } else {
          cleanupRef.current = dispose;
        }
      });
    });

    return () => {
      cancelled = true;
      cleanupRef.current?.();
      cleanupRef.current = null;
    };
  }, [isAuthenticated]);

  return <>{children}</>;
};
